/**
 * CLI 28 — Pestaña admin "Uso global" para rol admin.
 *
 * Dos vistas en la misma página:
 * - Por tenant (agregado por cliente).
 * - Por user (top consumidores + filtro por user_id).
 */
import { useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { FacturacionPanel } from "@/components/FacturacionPanel";
import { InfraTrafficPanel } from "@/components/InfraTrafficPanel";
import { QuotaMeter } from "@/components/QuotaMeter";
import { useBudget } from "@/hooks/useAnalysis";
import { FxEvolucion } from "@/components/FxEvolucion";
import { currentRate, fechaCorta, useFxRates, useFxSerie, useSetFxRate } from "@/hooks/useFx";
import {
  useUsageByTenant,
  useUsageByUser,
  useUsageCalls,
  type UsageFeatureBucket,
  type UsageFilters,
  type UsageTotals,
} from "@/hooks/useUsage";
import {
  featureLabel,
  formatDateTime,
  formatMxn,
  formatNumber,
  formatShortDate,
  formatUsd,
  projectToMonthEnd,
  rangePreset,
  tierLabel,
} from "@/lib/usageHelpers";


function parseFiltersFromUrl(params: URLSearchParams): UsageFilters {
  const granularityRaw = params.get("granularity") ?? "day";
  const granularity: "day" | "hour" = granularityRaw === "hour" ? "hour" : "day";
  return {
    since: params.get("since") ?? undefined,
    until: params.get("until") ?? undefined,
    tier: params.get("tier") ?? undefined,
    feature: params.get("feature") ?? undefined,
    granularity,
    tenant_id: params.get("tenant_id") ?? undefined,
    user_id: params.get("user_id") ?? undefined,
  };
}


export function AdminUsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFiltersFromUrl(searchParams), [searchParams]);
  const view = searchParams.get("view") ?? "by_tenant";

  const setRangePreset = (preset: "last_7_days" | "last_30_days") => {
    const range = rangePreset(preset);
    const next = new URLSearchParams(searchParams);
    if (range.since) next.set("since", range.since);
    if (range.until) next.set("until", range.until);
    setSearchParams(next, { replace: true });
  };

  const setQueryParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Uso global</h1>
        <p className="text-sm text-muted-foreground">
          Lo que cuesta operar AROC y lo que se le factura al cliente: consumo de IA por
          tenant y por usuario, y la cuota del servidor.
        </p>
      </div>

      <TokenBudgetMeter />

      <FxRateCard />

      <div className="flex flex-wrap gap-2 items-center">
        <button
          className={`px-3 py-1 text-sm rounded-md border ${
            view === "facturacion" ? "bg-primary text-primary-foreground" : "bg-card"
          }`}
          onClick={() => setQueryParam("view", "facturacion")}
        >
          Facturación
        </button>
        <button
          className={`px-3 py-1 text-sm rounded-md border ${
            view === "by_tenant" ? "bg-primary text-primary-foreground" : "bg-card"
          }`}
          onClick={() => setQueryParam("view", "by_tenant")}
        >
          Por tenant
        </button>
        <button
          className={`px-3 py-1 text-sm rounded-md border ${
            view === "by_user" ? "bg-primary text-primary-foreground" : "bg-card"
          }`}
          onClick={() => setQueryParam("view", "by_user")}
        >
          Por usuario
        </button>
        <button
          className={`px-3 py-1 text-sm rounded-md border ${
            view === "infraestructura" ? "bg-primary text-primary-foreground" : "bg-card"
          }`}
          onClick={() => setQueryParam("view", "infraestructura")}
        >
          Infraestructura
        </button>
        <button
          className="px-3 py-1 text-sm rounded-md border bg-card"
          onClick={() => setRangePreset("last_7_days")}
        >
          Últimos 7 días
        </button>
        <button
          className="px-3 py-1 text-sm rounded-md border bg-card"
          onClick={() => setRangePreset("last_30_days")}
        >
          Últimos 30 días
        </button>
        <label className="text-sm flex items-center gap-2">
          Desde:
          <input
            type="date"
            value={filters.since ?? ""}
            onChange={(e) => setQueryParam("since", e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
        <label className="text-sm flex items-center gap-2">
          Hasta:
          <input
            type="date"
            value={filters.until ?? ""}
            onChange={(e) => setQueryParam("until", e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
      </div>

      {view === "facturacion" && <FacturacionPanel />}
      {view === "by_tenant" && <ByTenantView filters={filters} />}
      {view === "infraestructura" && <InfraTrafficPanel />}
      {view === "by_user" && <ByUserView filters={filters} setQueryParam={setQueryParam} />}
    </div>
  );
}


/**
 * Medidor del tope mensual de tokens del motor de analisis.
 *
 * El endpoint (`/api/v1/analysis/budget`) y el hook `useBudget()` ya
 * existian; lo que faltaba era pintarlos. Va arriba de las pestanas
 * porque es una cuota de la instancia entera, no de un tenant ni de un
 * usuario, y por eso NO se filtra por el rango de fechas de abajo.
 */
function TokenBudgetMeter() {
  const { data, isLoading, error } = useBudget();
  if (isLoading || error || !data) return null;
  return (
    <QuotaMeter
      title="Tope mensual de tokens"
      scope="Motor de análisis · se cuenta sobre las sesiones del mes en curso (UTC), no sobre el detalle de llamadas de abajo"
      used={data.used}
      limit={data.limit}
      format={formatNumber}
      projected={data.enabled ? projectToMonthEnd(data.used) : null}
      resetsLabel={data.resets_at ? formatShortDate(data.resets_at) : null}
      disabled={!data.enabled}
    />
  );
}


function ByTenantView({ filters }: { filters: UsageFilters }) {
  const { data, isLoading, error } = useUsageByTenant(filters);
  if (isLoading) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  if (error) {
    return (
      <div className="text-sm text-destructive">No se pudo obtener: {(error as Error).message}</div>
    );
  }
  if (!data) return null;
  return (
    <>
      <Totals data={data.totals} />
      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-sm font-medium mb-3">Por tenant</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1">Tenant</th>
              <th className="py-1 text-right">Llamadas</th>
              <th className="py-1 text-right">Tokens</th>
              <th className="py-1 text-right">Costo</th>
            </tr>
          </thead>
          <tbody>
            {data.by_tenant.map((r) => (
              <tr key={r.tenant_id} className="border-t">
                <td className="py-1">{r.tenant_id === "_none" ? "(sin tenant)" : r.tenant_id}</td>
                <td className="py-1 text-right">{formatNumber(r.calls)}</td>
                <td className="py-1 text-right">{formatNumber(r.tokens)}</td>
                <td className="py-1 text-right">{formatUsd(r.estimated_cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


function ByUserView({
  filters,
  setQueryParam,
}: {
  filters: UsageFilters;
  setQueryParam: (key: string, value: string | null) => void;
}) {
  const { data, isLoading, error } = useUsageByUser(filters);
  if (isLoading) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  if (error) {
    return (
      <div className="text-sm text-destructive">No se pudo obtener: {(error as Error).message}</div>
    );
  }
  if (!data) return null;
  return (
    <>
      <Totals data={data.totals} />
      {filters.user_id && (
        <UsuarioEnFoco
          userId={filters.user_id}
          username={
            data.by_user.find((r) => r.user_id === filters.user_id)?.username ?? null
          }
          byFeature={data.by_feature}
          filters={filters}
          limpiar={() => setQueryParam("user_id", null)}
        />
      )}

      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-sm font-medium mb-3">Top consumidores</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1">Usuario</th>
              <th className="py-1 text-right">Llamadas</th>
              <th className="py-1 text-right">Tokens</th>
              <th className="py-1 text-right">Costo</th>
              <th className="py-1"></th>
            </tr>
          </thead>
          <tbody>
            {data.by_user.map((r) => (
              <tr key={r.user_id} className="border-t">
                <td className="py-1">{r.username ?? r.user_id.slice(0, 8)}</td>
                <td className="py-1 text-right">{formatNumber(r.calls)}</td>
                <td className="py-1 text-right">{formatNumber(r.tokens)}</td>
                <td className="py-1 text-right">{formatUsd(r.estimated_cost_usd)}</td>
                <td className="py-1 text-right">
                  <button
                    className="px-2 py-0.5 text-xs rounded-md border bg-card hover:bg-accent"
                    onClick={() => setQueryParam("user_id", r.user_id)}
                  >
                    Ver detalle
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


/**
 * El "de donde salieron los tokens" de UN usuario.
 *
 * Dos niveles: en que funcion se le fue el consumo (agregado), y despues
 * llamada por llamada. La tabla agregada de arriba contesta *cuanto*;
 * esto contesta *en que* y *cuando*.
 */
function UsuarioEnFoco({
  userId,
  username,
  byFeature,
  filters,
  limpiar,
}: {
  userId: string;
  username: string | null;
  byFeature: UsageFeatureBucket[];
  filters: UsageFilters;
  limpiar: () => void;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">
          De dónde salieron los tokens de{" "}
          <span className="font-semibold">{username ?? userId.slice(0, 8)}</span>
        </h2>
        <button
          className="px-2 py-0.5 text-xs rounded-md border bg-card hover:bg-accent"
          onClick={limpiar}
        >
          Quitar filtro
        </button>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          En qué se gastó
        </h3>
        {byFeature.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin consumo registrado en este rango.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1">Función</th>
                <th className="py-1 text-right">Llamadas</th>
                <th className="py-1 text-right">Tokens</th>
                <th className="py-1 text-right">Costo</th>
              </tr>
            </thead>
            <tbody>
              {byFeature.map((f) => (
                <tr key={f.feature} className="border-t">
                  <td className="py-1">{featureLabel(f.feature)}</td>
                  <td className="py-1 text-right">{formatNumber(f.calls)}</td>
                  <td className="py-1 text-right">{formatNumber(f.tokens)}</td>
                  <td className="py-1 text-right">{formatUsd(f.estimated_cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TablaDeLlamadas filters={filters} />
    </div>
  );
}


const LLAMADAS_POR_PAGINA = 25;

function TablaDeLlamadas({ filters }: { filters: UsageFilters }) {
  const [offset, setOffset] = useState(0);
  // Cambiar de usuario o de fechas sin regresar a la primera pagina deja
  // la tabla vacia con un "Anterior" que no se explica.
  const clave = `${filters.user_id ?? ""}|${filters.since ?? ""}|${filters.until ?? ""}`;
  const [claveVista, setClaveVista] = useState(clave);
  if (clave !== claveVista) {
    setClaveVista(clave);
    setOffset(0);
  }

  const { data, isLoading, error } = useUsageCalls(filters, {
    limit: LLAMADAS_POR_PAGINA,
    offset,
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Cargando llamadas…</div>;
  if (error) {
    return (
      <div className="text-sm text-destructive">
        No se pudieron obtener las llamadas: {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;
  if (data.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hay llamadas registradas en este rango.
      </p>
    );
  }

  const desde = data.offset + 1;
  const hasta = Math.min(data.offset + data.items.length, data.total);

  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Llamada por llamada
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1">Cuándo</th>
              <th className="py-1">Función</th>
              <th className="py-1">Motor</th>
              <th className="py-1 text-right">Tokens</th>
              <th className="py-1 text-right">Costo</th>
              <th className="py-1 text-right">Tardó</th>
              <th className="py-1">Resultado</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="py-1 whitespace-nowrap">{formatDateTime(c.ts)}</td>
                <td className="py-1">{featureLabel(c.feature)}</td>
                <td className="py-1">{tierLabel(c.tier)}</td>
                <td className="py-1 text-right">{formatNumber(c.tokens)}</td>
                <td className="py-1 text-right">
                  {c.estimated_cost_mxn === null
                    ? formatUsd(c.estimated_cost_usd)
                    : formatMxn(c.estimated_cost_mxn)}
                </td>
                <td className="py-1 text-right">{formatNumber(c.latency_ms)} ms</td>
                <td className="py-1">
                  {c.status === "success" ? (
                    "OK"
                  ) : (
                    <span className="text-destructive">{c.error_kind ?? c.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-xs text-muted-foreground">
          {desde}–{hasta} de {formatNumber(data.total)}
        </span>
        <div className="flex gap-2">
          <button
            className="px-2 py-0.5 text-xs rounded-md border bg-card hover:bg-accent disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LLAMADAS_POR_PAGINA))}
          >
            Anterior
          </button>
          <button
            className="px-2 py-0.5 text-xs rounded-md border bg-card hover:bg-accent disabled:opacity-40"
            disabled={hasta >= data.total}
            onClick={() => setOffset(offset + LLAMADAS_POR_PAGINA)}
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}


// El tipo venia escrito a mano aqui y por eso quedo desfasado al agregar el
// costo en pesos. Se usa el del hook, que es la unica fuente.
function Totals({ data }: { data: UsageTotals }) {
  const total =
    data.input_tokens + data.output_tokens + data.cache_read_tokens + data.cache_write_tokens;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCard label="Llamadas" value={formatNumber(data.calls)} />
      <StatCard label="Tokens totales" value={formatNumber(total)} />
      <StatCard label="Errores" value={formatNumber(data.errors)} />
      <CostStatCard usd={data.estimated_cost_usd} mxn={data.estimated_cost_mxn} />
    </div>
  );
}


/**
 * Costo con el peso como numero principal y la moneda de la factura debajo.
 *
 * Si falta el tipo de cambio, `mxn` llega en null — NO en cero. Entonces se
 * muestra la moneda original y se dice por que: inventar un 1:1 o pintar
 * $0.00 es exactamente el error que ya cometio la tabla de precios.
 */
function CostStatCard({ usd, mxn }: { usd: number; mxn: number | null }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        Costo estimado
      </div>
      {mxn === null ? (
        <>
          <div className="text-2xl font-semibold mt-1">{formatUsd(usd)}</div>
          <div className="text-xs text-amber-600 mt-1">
            Falta el tipo de cambio: se muestra la moneda original.
          </div>
        </>
      ) : (
        <>
          <div className="text-2xl font-semibold mt-1">{formatMxn(mxn)}</div>
          <div className="text-xs text-muted-foreground mt-1">{formatUsd(usd)}</div>
        </>
      )}
    </div>
  );
}


function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}


/**
 * Tipo de cambio vigente de cada par, de dónde salió y quién lo puso.
 *
 * Lo normal es el FIX diario de Banxico; la carga manual queda como respaldo
 * y gana desde que se fija hasta el siguiente FIX. Por eso la nota es
 * obligatoria aquí: un manual sin razón escrita no se puede re-explicar.
 *
 * El formulario se muestra a cualquier admin y el candado lo aplica el
 * servidor (403 `super_user_required`). Repetir la regla de permisos aqui
 * seria abrir la puerta a que las dos versiones se desincronicen.
 */
const PARES_FX: { from: string; nombre: string }[] = [
  { from: "USD", nombre: "dólar" },
  { from: "EUR", nombre: "euro" },
];

function FxRateCard() {
  const { data } = useFxRates();
  const set = useSetFxRate();
  const [par, setPar] = useState("USD");
  const [valor, setValor] = useState("");
  const [nota, setNota] = useState("");
  const [dias, setDias] = useState(30);
  const serie = useFxSerie(dias);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    const rate = Number(valor);
    if (!Number.isFinite(rate) || rate <= 0 || nota.trim() === "") return;
    set.mutate(
      { currency_from: par, currency_to: "MXN", rate, notes: nota.trim() },
      {
        onSuccess: () => {
          setValor("");
          setNota("");
        },
      },
    );
  };

  const error = set.error as { status?: number } | null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">Tipo de cambio a peso</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {data?.banxico_activo
              ? "FIX oficial de Banxico, cada día hábil"
              : "Sincronía con Banxico apagada: sólo carga manual"}
          </span>
          <div className="flex rounded-md border text-xs" role="group" aria-label="Periodo">
            {[30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDias(d)}
                aria-pressed={dias === d}
                className={`px-2 py-0.5 ${
                  dias === d ? "bg-primary text-primary-foreground" : "bg-card"
                }`}
              >
                {d} d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mt-2">
        {PARES_FX.map(({ from, nombre }) => {
          const vigente = currentRate(data, from, "MXN");
          const estado = data?.banxico?.find((b) => b.par === `${from}/MXN`);
          return (
            <div key={from} className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">
                {nombre} · {from}/MXN
              </div>
              {vigente ? (
                <>
                  <p className="text-2xl font-semibold tabular-nums">
                    {vigente.rate.toLocaleString("es-MX", { maximumFractionDigits: 4 })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {vigente.source === "banxico"
                      ? `FIX Banxico${estado ? ` ${estado.serie}` : ""} del ${fechaCorta(
                          estado?.ultimo_fix ?? vigente.effective_from,
                        )}`
                      : `Manual · ${vigente.updated_by_username ?? "—"} · ${new Date(
                          vigente.effective_from,
                        ).toLocaleString("es-MX")}${vigente.notes ? ` · «${vigente.notes}»` : ""}`}
                  </p>
                </>
              ) : (
                <p className="text-sm text-amber-600 mt-1">
                  Sin tipo de cambio: los costos en {from} se muestran en su moneda original.
                </p>
              )}
              {serie.data?.pares && (
                <FxEvolucion
                  par={`${from}/MXN`}
                  desde={serie.data.desde}
                  puntos={serie.data.pares.find((p) => p.par === `${from}/MXN`)?.puntos ?? []}
                />
              )}
              {estado?.alerta && (
                <p className="text-xs text-destructive mt-1">
                  {estado.dias_habiles_sin_dato} días hábiles sin dato nuevo de Banxico: se usa
                  el último publicado.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={enviar} className="flex flex-wrap items-end gap-2 mt-3">
        <label className="text-sm flex flex-col gap-1">
          Par
          <select
            value={par}
            onChange={(e) => setPar(e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          >
            {PARES_FX.map(({ from }) => (
              <option key={from} value={from}>
                {from}/MXN
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex flex-col gap-1">
          Manual
          <input
            type="number"
            step="0.0001"
            min="0"
            inputMode="decimal"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="px-2 py-1 text-sm rounded-md border w-32"
            placeholder="18.50"
          />
        </label>
        <label className="text-sm flex flex-col gap-1 flex-1 min-w-48">
          Nota (obligatoria)
          <input
            type="text"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="por qué se fija a mano"
            className="px-2 py-1 text-sm rounded-md border w-full"
          />
        </label>
        <button
          type="submit"
          disabled={set.isPending || valor === "" || nota.trim() === ""}
          className="px-3 py-1 text-sm rounded-md border bg-card disabled:opacity-50"
        >
          {set.isPending ? "Guardando…" : "Fijar"}
        </button>
      </form>

      {error && (
        <p className="text-sm text-destructive mt-2">
          {error.status === 403
            ? "Solo un super usuario puede fijar el tipo de cambio."
            : "No se pudo guardar."}
        </p>
      )}

      <p className="text-xs text-muted-foreground mt-2">
        Cada gasto se convierte con el tipo de cambio de su día (fin de semana o festivo: el
        último FIX publicado). Un manual gana desde que se fija hasta el siguiente FIX, y no
        borra nada: el histórico queda.
      </p>
    </div>
  );
}
